import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Dropdown, Form, Modal, Spinner, Table } from 'react-bootstrap';
import Header from '../layouts/Header';
import Footer from '../layouts/Footer';
import axiosInstance from '../services/axiosInterceptor';
import useStudyYearSelector from '../discipline/useStudyYearSelector';
import { useLanguage } from '../contexts/LanguageContext';
import { toISODate } from '../utils/thaiDate'

const UPLOAD_BASE = (process.env.REACT_APP_API || 'http://localhost:3002/api').replace(/\/api\/?$/, '').replace(/\/$/, '');
const todayISO = () => toISODate(new Date());

const StudentAvatar = ({ photo, name, arabicName }) => {
  const [hovered, setHovered] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const initial = name?.trim().charAt(0) || '?';
  const src = photo ? `${UPLOAD_BASE}${photo.startsWith('/') ? photo : `/${photo}`}` : null;
  const circleStyle = { width: 36, height: 36, borderRadius: '50%', flexShrink: 0 };

  return (
    <>
      <span
        style={{ position: 'relative', display: 'inline-block', lineHeight: 0 }}
        onMouseEnter={() => src && setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={() => src && setShowModal(true)}
      >
        {src ? (
          <img src={src} alt={name} style={{ ...circleStyle, objectFit: 'cover', cursor: 'pointer' }} />
        ) : (
          <span style={{ ...circleStyle, backgroundColor: '#adb5bd', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600 }}>
            {initial}
          </span>
        )}
        {src && hovered && (
          <div style={{ position: 'absolute', left: 44, top: '50%', transform: 'translateY(-50%)', zIndex: 1050, background: '#fff', borderRadius: 8, boxShadow: '0 4px 20px rgba(0,0,0,0.25)', padding: 4 }}>
            <img src={src} alt={name} style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 6, display: 'block' }} />
          </div>
        )}
      </span>
      {src && (
        <Modal show={showModal} onHide={() => setShowModal(false)} centered size="sm">
          <Modal.Body className="p-3 text-center">
            <img src={src} alt={name} style={{ maxWidth: '100%', maxHeight: '70vh', borderRadius: 8 }} />
            <div className="mt-2 fw-semibold small">{name}</div>
            {arabicName && <div className="arabic-text fw-semibold small" dir="rtl">{arabicName}</div>}
          </Modal.Body>
        </Modal>
      )}
    </>
  );
};

export default function AdvisorDormitory({ islamic = false }) {
  const setSkin = useState(localStorage.getItem('skin-mode') ? 'dark' : '')[1];
  const apiPath = islamic ? '/api/islamic-advisor/dormitory' : '/api/advisor/dormitory';
  const sectionLabel = islamic ? 'ข้อมูลประจำชั้นศาสนา' : 'ข้อมูลประจำชั้น';
  // หน้าเดียวกันนี้ใช้ทั้งภาคสามัญและภาคศาสนา — ชื่ออาหรับโผล่เฉพาะตอนกดสลับเป็นยาวี
  const { lang } = useLanguage();
  const showArabicName = islamic && lang === 'jawi';
  const { yearList, yearId, yearLabel, handleSelectYear } = useStudyYearSelector();
  const [data, setData] = useState({ rooms: [], villages: [] });
  const [roomId, setRoomId] = useState('');
  const [rowSelections, setRowSelections] = useState({});
  const [savingStudentId, setSavingStudentId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const loadData = useCallback(async () => {
    if (!yearId) return;
    setLoading(true);
    setError('');
    try {
      const response = await axiosInstance.get(`${apiPath}?yearId=${yearId}`);
      const nextData = response.data || { rooms: [], villages: [] };
      setData(nextData);
      setRoomId(previous => (
        nextData.rooms.some(room => String(room.r_id) === String(previous))
          ? previous
          : (nextData.rooms[0]?.r_id || '')
      ));
      setRowSelections({});
    } catch (err) {
      setError(err.response?.data?.message || 'ไม่สามารถโหลดข้อมูลจัดการหอพักได้');
    } finally {
      setLoading(false);
    }
  }, [apiPath, yearId]);

  useEffect(() => { loadData(); }, [loadData]);

  const currentRoom = useMemo(
    () => data.rooms.find(room => String(room.r_id) === String(roomId)) || null,
    [data.rooms, roomId]
  );
  const students = currentRoom?.students || [];

  useEffect(() => { setRowSelections({}); }, [roomId]);

  const getVillagesForStudent = (student) => (
    data.villages.filter(village => village.village_gender === student.st_gen)
  );

  const getRowSelection = (studentId) => rowSelections[studentId] || { villageId: '', dormId: '' };

  const selectVillage = (studentId, villageId) => {
    setRowSelections(previous => ({
      ...previous,
      [studentId]: { villageId, dormId: '' }
    }));
  };

  const selectDorm = (studentId, dormId) => {
    setRowSelections(previous => ({
      ...previous,
      [studentId]: { ...(previous[studentId] || { villageId: '', dormId: '' }), dormId }
    }));
  };

  const saveAllocation = async (student) => {
    const selection = getRowSelection(student.st_id);
    if (!selection.villageId || !selection.dormId) {
      setError(`กรุณาเลือกหมู่บ้านและห้องพักของ ${student.st_name}`);
      return;
    }
    const village = data.villages.find(item => String(item.village_id) === String(selection.villageId));
    const building = village?.buildings.find(item => String(item.dorm_id) === String(selection.dormId));
    const vacancy = building
      ? Number(building.capacity_total) - Number(building.current_occupancy)
      : 0;
    if (vacancy <= 0) {
      setError('ห้องพักที่เลือกเต็มแล้ว กรุณาเลือกห้องอื่น');
      return;
    }

    setSavingStudentId(student.st_id);
    setError('');
    setMessage('');
    try {
      await axiosInstance.post(`${apiPath}/allocate`, {
        yearId,
        roomId,
        dormId: selection.dormId,
        studentIds: [student.st_id],
        checkinDate: todayISO()
      });
      setMessage(`บันทึก ${student.st_name} เข้าหอพักเรียบร้อยแล้ว`);
      await loadData();
    } catch (err) {
      setError(err.response?.data?.message || 'ไม่สามารถบันทึกการเข้าหอพักได้');
    } finally {
      setSavingStudentId(null);
    }
  };

  return (
    <>
      {!islamic && <Header onSkin={setSkin} />}
      <main className={islamic ? '' : 'main main-app p-3 p-lg-4'}>
        <div className="d-sm-flex align-items-center justify-content-between mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item">{sectionLabel}</li>
              <li className="breadcrumb-item active">จัดการหอพัก</li>
            </ol>
            <h4 className="main-title mb-0">นำเข้านักเรียนในหอพัก</h4>
          </div>
          <Dropdown className="mt-3 mt-sm-0">
            <Dropdown.Toggle variant="primary" id="advisor-dormitory-year">
              {yearLabel || 'เลือกปีการศึกษา'}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {yearList.map(year => (
                <Dropdown.Item key={year.year_id} onClick={() => handleSelectYear(year.year_id)}>
                  ปีการศึกษา {year.year_year}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </div>

        {message && <Alert variant="success" dismissible onClose={() => setMessage('')}>{message}</Alert>}
        {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}


        <Card className="card-one">
          <Card.Header className="d-flex flex-wrap align-items-center gap-2">
            <Card.Title as="h6" className="mb-0">รายชื่อนักเรียน {currentRoom ? (currentRoom.room_label || `ม.${currentRoom.c_class}/${currentRoom.r_no}`) : ''}</Card.Title>
            <span className="ms-auto text-secondary fs-sm">ทั้งหมด {students.length} คน</span>
          </Card.Header>
          <Card.Body className="p-0">
            {loading ? (
              <div className="text-center py-5"><Spinner animation="border" /></div>
            ) : (
              <Table responsive hover className="mb-0 align-middle">
                <thead className="table-secondary">
                  <tr>
                    <th className="text-center" style={{ width: 65 }}>ลำดับ</th>
                    <th style={{ minWidth: 240 }}>นักเรียน</th>
                    <th className="text-center" style={{ width: 90 }}>เพศ</th>
                    {islamic && <th className="text-center" style={{ width: 110 }}>ชั้นสามัญ</th>}
                    <th style={{ minWidth: 190 }}>หอพักปัจจุบัน</th>
                    <th style={{ minWidth: 180 }}>เขต/หมู่บ้าน</th>
                    <th style={{ minWidth: 220 }}>ห้องพัก</th>
                    <th className="text-center" style={{ width: 120 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {students.length === 0 ? (
                    <tr><td colSpan={islamic ? 8 : 7} className="text-center text-secondary py-5">ไม่พบรายชื่อนักเรียน</td></tr>
                  ) : students.map((student, index) => {
                    const selection = getRowSelection(student.st_id);
                    const villages = getVillagesForStudent(student);
                    const selectedVillage = villages.find(village => String(village.village_id) === String(selection.villageId));
                    const buildings = selectedVillage?.buildings || [];
                    const isSaving = savingStudentId === student.st_id;
                    return (
                      <tr key={student.st_id} className={student.alloc_id ? 'table-success' : ''}>
                        <td className="text-center">{index + 1}</td>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <StudentAvatar photo={student.st_photo} name={student.st_name} arabicName={showArabicName ? student.st_arabicname : null} />
                            <div>
                              <div className="fw-semibold">{student.st_name}</div>
                              {showArabicName && (
                                <div className="arabic-text fw-semibold" dir="rtl">
                                  {student.st_arabicname || <span className="text-secondary fs-xs">-</span>}
                                </div>
                              )}
                              <div className="fs-xs text-secondary">{student.st_idnumber}</div>
                            </div>
                          </div>
                        </td>
                        <td className="text-center">
                          <Badge bg={student.st_gen === 'm' ? 'primary' : 'danger'}>{student.st_gen === 'm' ? 'ชาย' : 'หญิง'}</Badge>
                        </td>
                        {islamic && (
                          <td className="text-center">
                            {student.regular_class || <span className="text-secondary">-</span>}
                          </td>
                        )}
                        <td>
                          {student.alloc_id
                            ? <><Badge bg="success" className="me-2">เข้าหอแล้ว</Badge>{student.village_name} หอ {student.dorm_name}</>
                            : <span className="text-secondary">ยังไม่ได้จัดหอพัก</span>}
                        </td>
                        <td>
                          <Form.Select
                            size="sm"
                            value={selection.villageId}
                            onChange={event => selectVillage(student.st_id, event.target.value)}
                            disabled={isSaving}
                            aria-label={`เลือกหมู่บ้านของ ${student.st_name}`}
                          >
                            <option value="">เลือกหมู่บ้าน</option>
                            {villages.map(village => (
                              <option key={village.village_id} value={village.village_id}>{village.village_name}</option>
                            ))}
                          </Form.Select>
                        </td>
                        <td>
                          <Form.Select
                            size="sm"
                            value={selection.dormId}
                            onChange={event => selectDorm(student.st_id, event.target.value)}
                            disabled={!selection.villageId || isSaving}
                            aria-label={`เลือกห้องพักของ ${student.st_name}`}
                          >
                            <option value="">{selection.villageId ? 'เลือกห้องพัก' : 'เลือกหมู่บ้านก่อน'}</option>
                            {buildings.map(building => {
                              const vacancy = Number(building.capacity_total) - Number(building.current_occupancy);
                              return (
                                <option key={building.dorm_id} value={building.dorm_id} disabled={vacancy <= 0}>
                                  {building.dorm_name} (ว่าง {vacancy}/{building.capacity_total})
                                </option>
                              );
                            })}
                          </Form.Select>
                        </td>
                        <td className="text-center">
                          <Button
                            size="sm"
                            variant={student.alloc_id ? 'outline-primary' : 'primary'}
                            onClick={() => saveAllocation(student)}
                            disabled={!selection.dormId || isSaving || savingStudentId !== null}
                          >
                            {isSaving
                              ? <><Spinner animation="border" size="sm" className="me-1" />บันทึก</>
                              : <><i className={student.alloc_id ? 'ri-edit-line me-1' : 'ri-save-line me-1'} />{student.alloc_id ? 'แก้ไข' : 'บันทึก'}</>}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>
        {!islamic && <Footer />}
      </main>
    </>
  );
}